import React, { useEffect, useRef } from 'react';
import { trackLead } from '../lib/metaPixel';
import { trackAppointmentScheduled, resetAppointmentGuardIfStale } from '../lib/openaiPixel';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft } from 'lucide-react';

const AgendaPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const calendlyRef = useRef(null);

  useEffect(() => {
    resetAppointmentGuardIfStale();
    // Conversión SOLO cuando Calendly confirma la cita agendada (envío exitoso del formulario)
    const onCalendlyMessage = (e) => {
      if (e.origin.includes('calendly.com') && e.data?.event === 'calendly.event_scheduled') {
        trackLead();
        // event_id único de la cita de Calendly para deduplicación
        const inviteeUri = e.data?.payload?.invitee?.uri || e.data?.payload?.event?.uri || null;
        const eventId = inviteeUri ? `calendly_${inviteeUri.split('/').pop()}` : null;
        trackAppointmentScheduled(eventId);
      }
    };
    window.addEventListener('message', onCalendlyMessage);
    return () => window.removeEventListener('message', onCalendlyMessage);
  }, []);

  useEffect(() => {
    // Load Google Tag (gtag.js) with new ID
    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=AW-17859949825';
    document.head.appendChild(gtagScript);

    const gtagInlineScript = document.createElement('script');
    gtagInlineScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'AW-17859949825');
    `;
    document.head.appendChild(gtagInlineScript);

    // Lazy-load Calendly script only when this page is visited
    const initWidget = () => {
      if (window.Calendly && calendlyRef.current) {
        window.Calendly.initInlineWidget({
          url: 'https://calendly.com/crhome/diagnostico-de-interiorismo?primary_color=dc7109',
          parentElement: calendlyRef.current,
          prefill: {},
          utm: {}
        });
      }
    };

    if (window.Calendly) {
      initWidget();
    } else {
      let script = document.querySelector('script[src*="calendly.com/assets/external/widget.js"]');
      if (!script) {
        script = document.createElement('script');
        script.src = 'https://assets.calendly.com/assets/external/widget.js';
        script.async = true;
        document.head.appendChild(script);
      }
      script.addEventListener('load', initWidget);
    }

    return () => {
      if (gtagScript.parentNode) gtagScript.parentNode.removeChild(gtagScript);
      if (gtagInlineScript.parentNode) gtagInlineScript.parentNode.removeChild(gtagInlineScript);
    };
  }, []);

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header with back button */}
      <div className="bg-white border-b border-neutral-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-neutral-700 hover:text-neutral-900 transition-colors duration-200"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="text-sm font-light">
              {language === 'es' ? 'Volver al inicio' : 'Back to home'}
            </span>
          </button>
        </div>
      </div>

      {/* Title Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <p className="text-amber-600 text-sm font-light tracking-[0.3em] uppercase mb-4">
            {language === 'es' ? 'Agenda' : 'Schedule'}
          </p>
          <h1 className="text-4xl md:text-5xl font-light text-neutral-900 mb-4 tracking-tight">Agenda tu diagnóstico de interiorismo</h1>
          <p className="text-lg text-neutral-600 font-light max-w-2xl mx-auto">
            {language === 'es' 
              ? 'Selecciona el día y hora que mejor te convenga para tu diagnóstico de interiorismo.'
              : 'Select the day and time that works best for your interior design consultation.'}
          </p>
        </div>

        {/* Calendly Widget */}
        <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-2xl overflow-hidden">
          <div 
            ref={calendlyRef}
            style={{ minWidth: '320px', height: '700px' }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default AgendaPage;